import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dumbbell, Utensils, TrendingUp, Sparkles, ChevronRight, Zap, Target, Brain, Check, Star, Play, Clock, Shield, Users, ArrowRight, Quote } from "lucide-react";
import { useState } from "react";

const FEATURES = [
  { icon: Dumbbell, title: "85+ Øvelser", desc: "Komplet bibliotek med video guides til hver øvelse", color: "from-[#DFFF00]/20" },
  { icon: Sparkles, title: "AI Træner", desc: "Få skræddersyede programmer genereret på sekunder", color: "from-purple-500/20" },
  { icon: Utensils, title: "Madplaner", desc: "Ugentlige kostplaner med indkøbslister", color: "from-emerald-500/20" },
  { icon: TrendingUp, title: "Tracking", desc: "Log træning, vægt og krops-mål med grafer", color: "from-blue-500/20" },
  { icon: Target, title: "Mål", desc: "Sæt personlige mål og følg din fremgang", color: "from-orange-500/20" },
  { icon: Brain, title: "AI Ugeplaner", desc: "Komplette uge-programmer til træning og kost", color: "from-pink-500/20" },
];

const TESTIMONIALS = [
  { name: "Mikkel J.", role: "Tabte 12 kg på 3 mdr", quote: "AI træneren ændrede alt for mig. Jeg har aldrig haft så struktureret træning før!", avatar: "M", rating: 5 },
  { name: "Sofie L.", role: "Styrke +40%", quote: "Madplanerne er geniale - jeg behøver ikke tænke over hvad jeg skal spise.", avatar: "S", rating: 5 },
  { name: "Anders K.", role: "Fitness begynder", quote: "Perfekt til begyndere. Video guides til alle øvelser gjorde det nemt at starte.", avatar: "A", rating: 5 },
];

const RESULTS = [
  { metric: "12.847", label: "Træninger logget", sublabel: "af danske brugere" },
  { metric: "94%", label: "Anbefaler", sublabel: "til venner" },
  { metric: "4.8", label: "Rating", sublabel: "gennemsnit" },
  { metric: "3 min", label: "AI program", sublabel: "leveringstid" },
];

const INCLUDED = [
  "85+ øvelser med video",
  "AI-genererede træningsprogrammer",
  "Ugentlige madplaner",
  "Indkøbslister",
  "Krops-tracker med grafer",
  "Vand-tracker",
  "Ubegrænset AI-brug",
  "Mobil-venligt design",
];

const FAQ = [
  { q: "Kan jeg prøve gratis?", a: "Ja! Du får 3 dages fuld adgang helt gratis. Du tilføjer kort ved tilmelding, men betaler først efter prøveperioden." },
  { q: "Hvad koster det efter prøveperioden?", a: "Kun 49 kr/måned. Du kan afmelde når som helst - ingen binding." },
  { q: "Er det på dansk?", a: "Ja, hele platformen er 100% på dansk - både øvelser, madplaner og AI-coach." },
  { q: "Virker AI'en virkelig?", a: "Ja! Vores AI bruger GPT-5.2 til at generere personlige programmer baseret på dine mål og niveau." },
];

export default function LandingPage() {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="min-h-screen bg-[#09090b] overflow-x-hidden">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/50 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <Dumbbell className="h-6 w-6 text-[#DFFF00]" />
              <span style={{ fontFamily: 'Oswald, sans-serif' }} className="font-bold text-lg uppercase tracking-wider text-white">
                Fitrack<span className="text-[#DFFF00]">R</span>
              </span>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/login">
                <Button variant="ghost" className="text-zinc-400 hover:text-white text-sm">Log ind</Button>
              </Link>
              <Link to="/register">
                <Button className="bg-[#DFFF00] text-black hover:bg-[#C8E600] rounded-full font-bold text-xs uppercase tracking-wider h-9 px-5">
                  Prøv gratis
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative min-h-screen flex items-center pt-16">
        <div className="absolute inset-0 z-0">
          <img src="https://images.unsplash.com/photo-1534438327276-14e5300c3a48?crop=entropy&cs=srgb&fm=jpg&ixid=M3w0NjA3ODB8MHwxfHNlYXJjaHwxfHxneW0lMjBkYXJrfGVufDB8fHx8MTcwOTMwMDAwMHww&ixlib=rb-4.0.3&q=85"
            alt="Gym" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-[#09090b]/90 to-[#09090b]" />
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#DFFF00]/10 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="max-w-3xl">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-[#DFFF00]/10 border border-[#DFFF00]/20 rounded-full px-4 py-2 mb-8">
              <div className="w-2 h-2 rounded-full bg-[#DFFF00] animate-pulse" />
              <span className="text-xs font-bold text-[#DFFF00] uppercase tracking-wider">3 dage gratis • Først betaling efter prøveperiode</span>
            </div>

            <h1 style={{ fontFamily: 'Oswald, sans-serif' }} data-testid="hero-title"
              className="font-bold text-4xl sm:text-5xl lg:text-7xl uppercase tracking-tight text-white leading-[1.05]">
              Din <span className="text-[#DFFF00]">AI-drevne</span><br />
              fitness træner.<br />
              <span className="text-zinc-500">Altid i lommen.</span>
            </h1>

            <p className="mt-6 text-lg sm:text-xl text-zinc-400 max-w-xl leading-relaxed">
              Træningsprogrammer, madplaner og krops-tracking — alt genereret af AI og tilpasset <strong className="text-white">dine mål</strong>. 100% på dansk.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link to="/register" data-testid="hero-cta-register">
                <Button className="bg-[#DFFF00] text-black hover:bg-[#C8E600] hover:shadow-[0_0_30px_rgba(223,255,0,0.4)] rounded-xl font-bold uppercase tracking-wider h-14 px-8 text-sm w-full sm:w-auto transition-all duration-300">
                  Start gratis nu <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <a href="#features" className="flex items-center justify-center gap-2 text-zinc-400 hover:text-white transition-colors h-14">
                <Play className="h-5 w-5" /> Se hvordan det virker
              </a>
            </div>

            {/* Trust indicators */}
            <div className="mt-12 flex flex-wrap items-center gap-6 text-sm text-zinc-500">
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-emerald-400" />
                <span>Sikker betaling</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-blue-400" />
                <span>Afmeld når som helst</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-purple-400" />
                <span>1000+ danske brugere</span>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 rounded-full border-2 border-white/20 flex items-start justify-center p-2">
            <div className="w-1.5 h-3 rounded-full bg-[#DFFF00] animate-pulse" />
          </div>
        </div>
      </section>

      {/* Results/Stats */}
      <section className="py-16 border-y border-white/5 bg-gradient-to-r from-[#0c0c0f] via-[#18181b] to-[#0c0c0f]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {RESULTS.map(({ metric, label, sublabel }) => (
              <div key={label} className="text-center">
                <div style={{ fontFamily: 'JetBrains Mono, monospace' }} className="text-3xl sm:text-4xl font-bold text-[#DFFF00]">{metric}</div>
                <div className="text-sm font-bold text-white mt-1">{label}</div>
                <div className="text-xs text-zinc-500">{sublabel}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-xs font-bold text-[#DFFF00] uppercase tracking-widest">Funktioner</span>
            <h2 style={{ fontFamily: 'Oswald, sans-serif' }} className="font-bold text-3xl sm:text-4xl uppercase text-white mt-4">
              Alt du behøver.<br /><span className="text-zinc-500">Samlet ét sted.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map(({ icon: Icon, title, desc, color }) => (
              <div key={title} className={`relative overflow-hidden bg-gradient-to-br ${color} to-transparent bg-[#18181b] border border-white/5 rounded-2xl p-6 hover:border-[#DFFF00]/30 transition-all duration-300 hover:-translate-y-1 group`}>
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2 group-hover:bg-white/10 transition-all" />
                <div className="relative">
                  <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center mb-4 group-hover:bg-[#DFFF00]/10 transition-colors">
                    <Icon className="h-6 w-6 text-[#DFFF00]" />
                  </div>
                  <h3 style={{ fontFamily: 'Oswald, sans-serif' }} className="font-bold text-lg uppercase tracking-wider text-white mb-2">{title}</h3>
                  <p className="text-sm text-zinc-400 leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof - Testimonials */}
      <section className="py-24 bg-gradient-to-b from-[#09090b] via-[#0f0f12] to-[#09090b]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-xs font-bold text-[#DFFF00] uppercase tracking-widest">Anmeldelser</span>
            <h2 style={{ fontFamily: 'Oswald, sans-serif' }} className="font-bold text-3xl sm:text-4xl uppercase text-white mt-4">
              Det siger vores brugere
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS.map(({ name, role, quote, avatar, rating }) => (
              <div key={name} className="bg-[#18181b] border border-white/5 rounded-2xl p-6 hover:border-white/10 transition-all">
                <div className="flex gap-1 mb-4">
                  {[...Array(rating)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-[#DFFF00] text-[#DFFF00]" />
                  ))}
                </div>
                <Quote className="h-8 w-8 text-[#DFFF00]/20 mb-3" />
                <p className="text-zinc-300 leading-relaxed mb-6">"{quote}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#DFFF00]/20 to-[#DFFF00]/5 flex items-center justify-center">
                    <span className="font-bold text-[#DFFF00]">{avatar}</span>
                  </div>
                  <div>
                    <p className="font-bold text-white text-sm">{name}</p>
                    <p className="text-xs text-[#DFFF00]">{role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-xs font-bold text-[#DFFF00] uppercase tracking-widest">Pris</span>
            <h2 style={{ fontFamily: 'Oswald, sans-serif' }} className="font-bold text-3xl sm:text-4xl uppercase text-white mt-4">
              Simpelt og fair
            </h2>
            <p className="text-zinc-400 mt-4">Ingen skjulte gebyrer. Afmeld når som helst.</p>
          </div>

          <div className="max-w-md mx-auto">
            <div className="relative bg-gradient-to-b from-[#18181b] to-[#141417] border-2 border-[#DFFF00]/30 rounded-3xl p-8 overflow-hidden">
              {/* Popular badge */}
              <div className="absolute top-0 right-0">
                <div className="bg-[#DFFF00] text-black text-xs font-bold uppercase tracking-wider px-4 py-1.5 rounded-bl-xl">
                  Mest populær
                </div>
              </div>

              <div className="text-center mb-8">
                <p className="text-sm text-zinc-400 mb-2">FitrackR Pro</p>
                <div className="flex items-center justify-center gap-2">
                  <span style={{ fontFamily: 'JetBrains Mono, monospace' }} className="text-6xl font-bold text-white">49</span>
                  <div className="text-left">
                    <span className="text-xl text-zinc-300 block leading-tight">kr</span>
                    <span className="text-xs text-zinc-500 block">/måned</span>
                  </div>
                </div>
                <div className="inline-flex items-center gap-2 bg-emerald-500/10 text-emerald-400 text-sm font-bold px-4 py-2 rounded-full mt-4">
                  <Zap className="h-4 w-4" /> 3 dage gratis prøveperiode
                </div>
              </div>

              <div className="space-y-3 mb-8">
                {INCLUDED.map(item => (
                  <div key={item} className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-[#DFFF00]/10 flex items-center justify-center flex-shrink-0">
                      <Check className="h-3 w-3 text-[#DFFF00]" />
                    </div>
                    <span className="text-sm text-zinc-300">{item}</span>
                  </div>
                ))}
              </div>

              <Link to="/register" data-testid="pricing-cta">
                <Button className="w-full bg-[#DFFF00] text-black hover:bg-[#C8E600] hover:shadow-[0_0_30px_rgba(223,255,0,0.4)] rounded-xl font-bold uppercase tracking-wider h-14 text-sm transition-all duration-300">
                  Start gratis prøveperiode <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>

              <p className="text-center text-xs text-zinc-500 mt-4">
                Du betaler først efter 3 dages gratis prøveperiode
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-[#0c0c0f]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-xs font-bold text-[#DFFF00] uppercase tracking-widest">FAQ</span>
            <h2 style={{ fontFamily: 'Oswald, sans-serif' }} className="font-bold text-3xl uppercase text-white mt-4">
              Ofte stillede spørgsmål
            </h2>
          </div>

          <div className="space-y-3">
            {FAQ.map(({ q, a }, i) => (
              <div key={i} className="bg-[#18181b] border border-white/5 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-5 text-left"
                >
                  <span className="font-bold text-white">{q}</span>
                  <ChevronRight className={`h-5 w-5 text-zinc-400 transition-transform ${openFaq === i ? 'rotate-90' : ''}`} />
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-5 pt-0">
                    <p className="text-zinc-400 text-sm leading-relaxed">{a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#DFFF00]/5 via-transparent to-[#DFFF00]/5" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-[#DFFF00]/10 rounded-full blur-3xl" />
        
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 style={{ fontFamily: 'Oswald, sans-serif' }} className="font-bold text-3xl sm:text-5xl uppercase text-white mb-6">
            Klar til at<br /><span className="text-[#DFFF00]">transformere</span> dig selv?
          </h2>
          <p className="text-zinc-400 mb-10 text-lg max-w-md mx-auto">
            Tilmeld dig nu og få 3 dages fuld adgang helt gratis. Du betaler først efter prøveperioden.
          </p>
          <Link to="/register" data-testid="final-cta">
            <Button className="bg-[#DFFF00] text-black hover:bg-[#C8E600] hover:shadow-[0_0_40px_rgba(223,255,0,0.5)] rounded-xl font-bold uppercase tracking-wider h-16 px-12 text-base transition-all duration-300">
              <Zap className="mr-2 h-5 w-5" /> Start din rejse nu
            </Button>
          </Link>
          <p className="text-xs text-zinc-500 mt-6">
            Allerede over 1000 danskere har startet deres fitnessrejse med FitrackR
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-12 bg-[#09090b]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-8">
            {/* Top Row */}
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-2">
                <Dumbbell className="h-5 w-5 text-[#DFFF00]" />
                <span style={{ fontFamily: 'Oswald, sans-serif' }} className="uppercase tracking-wider text-zinc-400">FitrackR</span>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-zinc-500">
                <Link to="/terms" className="hover:text-[#DFFF00] transition-colors">Vilkår & Betingelser</Link>
                <Link to="/privacy" className="hover:text-[#DFFF00] transition-colors">Privatlivspolitik</Link>
                <Link to="/cookies" className="hover:text-[#DFFF00] transition-colors">Cookie-politik</Link>
                <Link to="/handelsbetingelser" className="hover:text-[#DFFF00] transition-colors">Handelsbetingelser</Link>
              </div>
            </div>
            {/* Bottom Row */}
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-6 border-t border-white/5">
              <p className="text-xs text-zinc-600 text-center">
                © 2025 FitrackR. Din AI-drevne fitness platform. Bygget i Danmark.
              </p>
              <p className="text-xs text-zinc-600">
                CVR: [CVR-NUMMER] • <a href="mailto:kontakt@fitrackr.dk" className="hover:text-[#DFFF00]">[EMAIL]</a>
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
